// Brief Prospection Phase 1 §2 — Page de confirmation après clic magic link.
// Statuts : ok, already, full, past, expired, invalid (query param ?status=).
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, CalendarPlus, AlertTriangle, Clock, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtFull = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })
    + ' à ' + d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};

const CONTENT = {
  ok: { icon: Check, color: '#1D9E75', title: 'Votre place est confirmée 🎉' },
  already: { icon: Check, color: '#1D9E75', title: 'Vous êtes déjà inscrit·e ✓' },
  full: { icon: AlertTriangle, color: '#BA7517', title: 'Cette session est complète' },
  past: { icon: Clock, color: '#6b7280', title: 'Cet événement est déjà passé' },
  expired: { icon: Clock, color: '#BA7517', title: 'Ce lien de confirmation a expiré' },
  invalid: { icon: XCircle, color: '#dc2626', title: 'Lien invalide' },
};

export default function GuestConfirmedPage() {
  const [params] = useSearchParams();
  const status = params.get('status') || 'invalid';
  const eventId = params.get('event');
  const handle = params.get('handle');
  const slug = params.get('slug');
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    axios.get(`${API}/public/event/${eventId}`).then((r) => setEvent(r.data)).catch(() => {});
  }, [eventId]);

  const meta = CONTENT[status] || CONTENT.invalid;
  const Icon = meta.icon;
  const isSuccess = status === 'ok' || status === 'already';

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-testid="guest-confirmed-page">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sm:p-10 max-w-md w-full text-center">
        <div
          className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: `${meta.color}18` }}
        >
          <Icon className="h-8 w-8" style={{ color: meta.color }} />
        </div>
        <h1 className="text-xl font-extrabold text-gray-900" data-testid="guest-confirmed-title">{meta.title}</h1>

        {event && isSuccess && (
          <div className="mt-4 p-4 rounded-2xl bg-gray-50 text-left" data-testid="guest-confirmed-event">
            <p className="font-bold text-gray-900">{event.title}</p>
            <p className="text-sm text-gray-500 capitalize mt-0.5">{fmtFull(event.start_time)}</p>
            {event.expert?.display_name && (
              <p className="text-sm text-gray-500 mt-0.5">avec {event.expert.display_name}</p>
            )}
          </div>
        )}

        {isSuccess && (
          <>
            <p className="mt-4 text-sm text-gray-500 leading-relaxed">
              Un email de confirmation avec le <strong>lien visio</strong> et l'invitation
              calendrier vient de vous être envoyé.
            </p>
            {eventId && (
              <a
                href={`${API}/calendar/events/${eventId}/ics`}
                className="mt-5 inline-flex items-center justify-center gap-2 w-full py-3 rounded-2xl text-white font-bold shadow-md hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#7F77DD' }}
                data-testid="guest-confirmed-ics"
              >
                <CalendarPlus className="h-4 w-4" /> Ajouter à mon calendrier
              </a>
            )}
          </>
        )}

        {status === 'expired' && handle && slug && (
          <Link
            to={`/@${handle}/e/${slug}`}
            className="mt-5 inline-block w-full py-3 rounded-2xl text-white font-bold shadow-md hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#7F77DD' }}
            data-testid="guest-confirmed-rebook"
          >
            Refaire ma réservation
          </Link>
        )}

        {handle && (
          <Link
            to={`/@${handle}`}
            className="mt-3 inline-block text-sm font-semibold text-[#7F77DD]"
            data-testid="guest-confirmed-profile-link"
          >
            {event?.expert?.display_name
              ? `Découvrir les autres sessions de ${event.expert.display_name} →`
              : 'Voir le profil de l\'expert →'}
          </Link>
        )}

        <p className="mt-8 text-xs text-gray-300">
          <Link to="/" className="hover:text-gray-500 transition-colors">Propulsé par <span className="font-semibold">OpenSapiens</span></Link>
        </p>
      </div>
    </div>
  );
}
